import { useMemo } from "react";

import { setDynamicsNotification } from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/dynamics";
import { setHsNotification } from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/hubspot";
import { setSfNotification } from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/salesforce";
import { saveDynamicsAllSettingsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/export";
import {
    putDynamicsAccountsToggleThunk,
    putDynamicsCompanyAsLeadsToggleThunk,
    putDynamicsContactsToggleThunk,
    putDynamicsLeadsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/toggles";
import { saveHsAllSettingsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/export";
import {
    putHsCompaniesToggleThunk,
    putHsContactsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/toggles";
import { saveSfAllSettingsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/export";
import {
    putSfAccountsToggleThunk,
    putSfCompanyAsLeadsToggleThunk,
    putSfContactsToggleThunk,
    putSfLeadsToggleThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/toggles";
import {
    selectDynamicsCurrentSettings,
    selectDynamicsIsLoadingSettings,
    selectDynamicsIsTabsWithEmptyFields,
    selectDynamicsIsSavingSettings,
    selectDynamicsMissingRequiredFieldsCount,
    selectDynamicsSettingsChangedTabNames,
    selectDynamicsTabsWithEmptyFields,
    selectDynamicsTotalSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamics/exportSelectors";
import {
    selectHsIsLoadingSettings,
    selectHsIsSavingSettings,
    selectHsMissingRequiredFieldsCount,
    selectHsTotalSettings,
    selectHsIsTabsWithEmptyFields,
    selectHsSettingsChangedTabNames,
    selectHsTabsWithEmptyFields,
    selectHsCurrentSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspot/exportSelectors";
import {
    selectSfIsSavingSettings,
    selectTotalSettings,
    selectIsLoadingSettings,
    selectSfMissingRequiredFieldsCount,
    selectSfIsTabsWithEmptyFields,
    selectSfSettingsChangedTabNames,
    selectSfTabsWithEmptyFields,
    selectSfCurrentSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

export const useManageExportProps = (integrationName: Integration) => {
    const allProps = useMemo(
        () =>
            ({
                salesforce: {
                    setNotificationAction: setSfNotification,
                    isSaveLoadingState: selectSfIsSavingSettings,
                    isLoadingState: selectIsLoadingSettings,
                    putIsRecordExportEnabled: {
                        accounts: putSfAccountsToggleThunk,
                        contacts: putSfContactsToggleThunk,
                        leads: putSfLeadsToggleThunk,
                        companyAsLeads: putSfCompanyAsLeadsToggleThunk,
                    },
                    selectMissingRequiredFieldsCount: selectSfMissingRequiredFieldsCount,
                    selectSettingsCount: selectTotalSettings,
                    saveAllExportSettings: saveSfAllSettingsThunk,
                    changedTabNames: selectSfSettingsChangedTabNames,
                    isEmptyFieldsExist: selectSfIsTabsWithEmptyFields,
                    tabsWithEmptyFields: selectSfTabsWithEmptyFields,
                    selectSettings: selectSfCurrentSettings,
                },
                hubspot: {
                    setNotificationAction: setHsNotification,
                    isSaveLoadingState: selectHsIsSavingSettings,
                    isLoadingState: selectHsIsLoadingSettings,
                    putIsRecordExportEnabled: {
                        accounts: putHsCompaniesToggleThunk,
                        contacts: putHsContactsToggleThunk,
                    },
                    selectMissingRequiredFieldsCount: selectHsMissingRequiredFieldsCount,
                    selectSettingsCount: selectHsTotalSettings,
                    saveAllExportSettings: saveHsAllSettingsThunk,
                    changedTabNames: selectHsSettingsChangedTabNames,
                    isEmptyFieldsExist: selectHsIsTabsWithEmptyFields,
                    tabsWithEmptyFields: selectHsTabsWithEmptyFields,
                    selectSettings: selectHsCurrentSettings,
                },
                dynamics: {
                    setNotificationAction: setDynamicsNotification,
                    isSaveLoadingState: selectDynamicsIsSavingSettings,
                    isLoadingState: selectDynamicsIsLoadingSettings,
                    putIsRecordExportEnabled: {
                        accounts: putDynamicsAccountsToggleThunk,
                        contacts: putDynamicsContactsToggleThunk,
                        leads: putDynamicsLeadsToggleThunk,
                        companyAsLeads: putDynamicsCompanyAsLeadsToggleThunk,
                    },
                    selectMissingRequiredFieldsCount: selectDynamicsMissingRequiredFieldsCount,
                    selectSettingsCount: selectDynamicsTotalSettings,
                    saveAllExportSettings: saveDynamicsAllSettingsThunk,
                    changedTabNames: selectDynamicsSettingsChangedTabNames,
                    isEmptyFieldsExist: selectDynamicsIsTabsWithEmptyFields,
                    tabsWithEmptyFields: selectDynamicsTabsWithEmptyFields,
                    selectSettings: selectDynamicsCurrentSettings,
                },
            })[integrationName],
        [integrationName],
    );

    return allProps;
};
